<script setup lang="ts">
import {computed, onMounted, ref} from "vue";
import {useRouter} from "vue-router";
import YoutubeChannelService from "@/services/YoutubeChannelService";
import type {YoutubeChannel} from "@/models/youtube_channel";
import mdmService from "@/services/MdmService";
import {store} from "@/store";
import {RouteNames} from "@/router/routeNames";
import Bugsnag from "@bugsnag/js";
import {useToast} from "vue-toastification";

const router = useRouter();
const toast = useToast();

const channels = store.channels;
const searchQuery = store.channelSearchQuery;
const selectedTags = store.selectedTags;

const loading = ref<boolean>(false);
const error = ref<any>();

const tags = computed(() => {
  const tagNames = new Set<string>();
  for (const channel of channels.value) {
    for (const tag of channel.tags) {
      tagNames.add(tag.name);
    }
  }
  return Array.from(tagNames).sort((a, b) => a.localeCompare(b));
});

const filteredChannels = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  const matchingChannels: YoutubeChannel[] = [];
  for (const channel of channels.value) {
    if (query && !channel.name.toLowerCase().includes(query)) {
      continue;
    }
    if (selectedTags.value.length > 0 && !hasSelectedTag(channel)) {
      continue;
    }
    matchingChannels.push(channel);
  }
  return matchingChannels;
});

onMounted(() => {
  loadChannels();
})

async function loadChannels() {
  loading.value = true;
  try {
    store.setChannels(await YoutubeChannelService.list());
  } catch (err: any) {
    console.error("Error in loadChannels: ", err);
    error.value = err;
    toast.error("An error occurred while loading channels. Please refresh the page and try again.");
    Bugsnag.notify(err);
  } finally {
    loading.value = false;
  }
}

function hasSelectedTag(channel: YoutubeChannel) {
  return channel.tags.some(tag => selectedTags.value.includes(tag.name));
}

function onSearchInput(event: Event) {
  store.setChannelSearchQuery((event.target as HTMLInputElement).value);
}

function logChannelSearch() {
  const query = searchQuery.value.trim();
  if (!query) {
    return;
  }
  mdmService.sendActivityLog('SearchedChannels', query);
}

function clearSearch() {
  store.setChannelSearchQuery('');
}

async function goToChannelPage(channelId: string) {
  await router.push({
    name: RouteNames.Channel,
    params: {
      channelId: channelId,
    }
  });
}
</script>

<template>
  <div class="info-container">
    <h1 class="page-title">Channels</h1>

    <div class="branding-info">
      <img class="branding-image" src="@/assets/made_purple_logo.png" alt="Made Purple Ltd">
      <span class="branding-title">powered by Made Purple</span>
    </div>
  </div>

  <div class="search-bar-wrapper">
    <form class="search-container" @submit.prevent="logChannelSearch">
      <input type="text" :value="searchQuery" placeholder="Search channels" @input="onSearchInput"/>
      <button class="clear-search" type="button" v-if="searchQuery.trim().length >= 1" @click="clearSearch">Clear</button>
    </form>

    <div class="tag-container" v-if="tags.length > 0">
      <button
          v-for="tag in tags"
          :key="tag"
          type="button"
          class="tag-chip"
          :class="{'tag-chip-selected': selectedTags.includes(tag)}"
          @click="store.toggleSelectedTag(tag)"
      >
        {{ tag }}
      </button>
    </div>
  </div>

  <template v-if="error">
    <h1 class="text-message">
      An unexpected error occurred. Please refresh the page and try again.
    </h1>
  </template>

  <template v-else-if="!loading && filteredChannels.length <= 0">
    <h1 class="text-message">No channels found</h1>
  </template>

  <template v-else>
    <div class="grid-container">
      <div v-for="channel in filteredChannels" :key="channel.channel_id" class="grid-item"
           @click="goToChannelPage(channel.channel_id)">
        <img :src="channel.image_url" :alt="channel.name"/>
        <div class="channel-name">{{ channel.name }}</div>
      </div>
    </div>
  </template>

  <div class="loading-bar-bottom" v-if="loading && !error">
    <div class="loading-bar-progress"></div>
  </div>
</template>

<style scoped>
.info-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.page-title {
  padding: 16px;
  font-size: 24px;
  font-weight: bold;
}

.branding-info {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.branding-image {
  width: 76px;
  height: 76px;
  object-fit: contain;
  margin-right: 16px;
}

.branding-title {
  font-size: 12px;
}

.search-bar-wrapper {
  position: sticky;
  top: 0;
  z-index: 100;
  background-color: var(--color-background);
  padding-bottom: 8px;
  padding-top: 8px;
  margin-bottom: 8px;
}

.search-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 16px;
  margin-right: 16px;
}

.search-container input {
  flex: 1;
  padding: 8px;
  border: 1px solid #cccccc;
  border-radius: 4px;
  font-size: 14px;
}

.search-container input:focus {
  outline: none;
}

.clear-search {
  margin-left: 8px;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  background-color: grey;
  border: none;
  color: white;
  font-weight: bold;
}

.clear-search:hover {
  background-color: darkgrey;
}

.tag-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 12px 16px 0;
}

.tag-chip {
  padding: 6px 14px;
  border: 1px solid #cccccc;
  border-radius: 999px;
  background-color: transparent;
  color: var(--color-text);
  font-size: 14px;
  cursor: pointer;
}

.tag-chip:hover {
  border-color: #b176b7;
}

.tag-chip-selected {
  background-color: #b176b7;
  border-color: #b176b7;
  color: white;
  font-weight: bold;
}

.grid-container {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  grid-gap: 16px;
  padding: 16px;
  max-width: 100%;
  margin: 0 auto;
}

.grid-item {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  cursor: pointer;
}

.grid-item img {
  object-fit: cover;
  width: 100%;
  aspect-ratio: 1;
  border-radius: 16px;
  margin-bottom: 8px;
}

.channel-name {
  padding-left: 8px;
  padding-right: 8px;
  font-size: 16px;
  font-weight: bold;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

@media (max-width: 1024px) {
  .grid-container {
    grid-template-columns: repeat(6, 1fr);
  }
}

@media (max-width: 768px) {
  .grid-container {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 512px) {
  .grid-container {
    grid-template-columns: repeat(2, 1fr);
  }
}

.text-message {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
</style>
